"use client";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../../../navigation/Layout/Layout";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import "./DoctorProfile.css";
import useLoadFollows from "../../hooks/useLoadFollows";
import useRendezvous from "../../../appointments/hooks/useRendezvous";

import DoctorHeaderCard from "../../componants/DoctorProfilePage/DoctorHeaderCard/DoctorHeaderCard";
import DoctorTabs from "../../componants/DoctorProfilePage/DoctorTabs/DoctorTabs";
import DoctorInfoPanel from "../../componants/DoctorProfilePage/DoctorInfoPanel/DoctorInfoPanel";
import AppointmentBookingCard from "../../componants/DoctorProfilePage/AppointmentBookingCard/AppointmentBookingCard";

import { fetchDoctorById } from "../../../authDoctor/redux/thunks/doctorsThunks";
import { getFollowedDoctorsThunk } from "../../../ProfileDoctor/redux/thunks/followsThunks";

export default function DoctorProfile() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { doctorId } = useParams();
  useLoadFollows();

  const [activeTab, setActiveTab] = useState("info");
  const [isPatientOfDoctor, setIsPatientOfDoctor] = useState(false);

  const { getConfirmedDoctorIdsForPatient } = useRendezvous();

  // ✅ Session docteur active ? -> layout docteur, sinon layout patient.
  const currentDoctorUser = useSelector((s) => s.doctor?.user);
  const isDoctorSession = !!currentDoctorUser;
  const Layout = isDoctorSession ? AppLayoutDoctor : AppLayout;

  // ✅ La carte de réservation n'est visible que pour un patient (pas un docteur)
  const canBookAppointment = !isDoctorSession;

  const currentUserId = useSelector((s) => {
    return s.auth?.user?.id || s.auth?.user?._id || s.user?.id || s.user?._id;
  });
  const isAuthenticated = useSelector((s) => s.auth?.isAuthenticated) || !!currentUserId;

  const doctorsSlice = useSelector((state) => state.doctors || state.doctor);

  const doctor = doctorsSlice?.selectedDoctor;
  const loading = doctorsSlice?.loadingSelectedDoctor;
  const error = doctorsSlice?.errorSelectedDoctor;

  useEffect(() => {
    if (doctorId) {
      dispatch(fetchDoctorById({ id: doctorId }));
    }
  }, [dispatch, doctorId]);

  useEffect(() => {
    if (isAuthenticated && currentUserId) {
      dispatch(
        getFollowedDoctorsThunk({
          followerId: String(currentUserId),
        })
      );
    }
  }, [dispatch, currentUserId, isAuthenticated]);

  useEffect(() => {
    // Un docteur n'a pas besoin de vérifier s'il est patient d'un autre docteur
    if (isDoctorSession) {
      setIsPatientOfDoctor(false);
      return;
    }
    if (!isAuthenticated || !currentUserId || !doctorId) return;

    getConfirmedDoctorIdsForPatient(currentUserId)
      .then((ids) => {
        const confirmed = ids.map(String).includes(String(doctorId));
        setIsPatientOfDoctor(confirmed);
      })
      .catch(() => setIsPatientOfDoctor(false));
  }, [isAuthenticated, currentUserId, doctorId, isDoctorSession]);

  if (loading) {
    return (
      <Layout>
        <div className="dp-page">
          <div className="dp-loading">
            <div className="dp-spinner"></div>
            <p>{t("doctorProfile.loading")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="dp-page">
          <div className="dp-error">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="dp-error-icon"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p className="dp-error-message">{t("doctorProfile.error")}</p>
            <p className="dp-error-detail">{String(error)}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!doctor) {
    return (
      <Layout>
        <div className="dp-page">
          <div className="dp-not-found">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="dp-not-found-icon"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <p className="dp-not-found-message">{t("doctorProfile.notFound")}</p>
            <p className="dp-not-found-suggestion">{t("doctorProfile.notFoundSuggestion")}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dp-page">
        <DoctorHeaderCard
          doctor={doctor}
          doctorIdOverride={doctorId}
          showEdit={false}
          isPatientOfDoctor={isPatientOfDoctor}
          isDoctorSession={isDoctorSession}
        />
        <DoctorTabs active={activeTab} onChange={setActiveTab} />

        <div className={`dp-grid ${!canBookAppointment ? "dp-grid--single" : ""}`}>
          <div className="dp-left">
            <DoctorInfoPanel doctor={doctor} activeTab={activeTab} />
          </div>

          {/* ✅ Carte de réservation masquée pour un docteur */}
          {canBookAppointment && (
            <div className="dp-right">
              <AppointmentBookingCard
                doctor={doctor}
                onBooked={(payload) => {}}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}